import React, { useState } from 'react';
import axios from 'axios';
import API_URL from '../config';
import { formatMoney } from '../utils/formatMoney';

const Ledger = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterType, setFilterType] = useState('month'); // 'month' or 'dateRange'
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);

  const buildLedgerQueryParams = () => {
    const params = new URLSearchParams();
    if (filterType === 'month') {
      params.append('year', year);
      params.append('month', month);
    } else {
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
    }
    return params;
  };

  const handleFilter = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = buildLedgerQueryParams();

      const response = await axios.get(
        `${API_URL}/api/ledger/ledger?${params.toString()}`
      );
      setResults(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch ledger data');
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  const readBlobErrorMessage = async (blob) => {
    try {
      const text = await blob.text();
      try {
        const j = JSON.parse(text);
        return j.error || j.message || text.slice(0, 300);
      } catch {
        return text.trim().slice(0, 300) || 'Export failed';
      }
    } catch {
      return 'Export failed';
    }
  };

  const handleExport = async () => {
    setExporting(true);
    setError(null);
    try {
      const params = buildLedgerQueryParams();
      const response = await axios.get(
        `${API_URL}/api/ledger/export?${params.toString()}`,
        { responseType: 'blob' }
      );

      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data]);

      const sig = new Uint8Array(await blob.slice(0, 4).arrayBuffer());
      const looksLikeZip =
        sig[0] === 0x50 && sig[1] === 0x4b && sig[2] === 0x03 && sig[3] === 0x04;
      if (!looksLikeZip) {
        setError(
          `${await readBlobErrorMessage(blob)} — Check REACT_APP_API_URL and restart the API server if you recently updated it.`
        );
        return;
      }

      let fileName = 'ledger-export.xlsx';
      if (filterType === 'month') {
        fileName = `ledger-${year}-${String(month).padStart(2, '0')}.xlsx`;
      } else if (startDate && endDate) {
        fileName = `ledger-${startDate}-to-${endDate}.xlsx`;
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const data = err.response?.data;
      if (data instanceof Blob) {
        setError(await readBlobErrorMessage(data));
      } else if (!err.response) {
        setError(
          err.message === 'Network Error'
            ? 'Cannot reach the API. Check the server is running and REACT_APP_API_URL matches its host and port.'
            : err.message || 'Failed to export ledger'
        );
      } else {
        setError(err.response?.data?.error || 'Failed to export ledger');
      }
    } finally {
      setExporting(false);
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);

  return (
    <div>
      <div className="card">
        <h2 style={{ marginBottom: '20px' }}>Ledger</h2>

        {error && (
          <div className="alert alert-error">{error}</div>
        )}

        <div className="radio-group">
          <label>
            <input
              type="radio"
              value="month"
              checked={filterType === 'month'}
              onChange={(e) => setFilterType(e.target.value)}
            />
            Filter by Month/Year
          </label>
          <label>
            <input
              type="radio"
              value="dateRange"
              checked={filterType === 'dateRange'}
              onChange={(e) => setFilterType(e.target.value)}
            />
            Filter by Date Range
          </label>
        </div>

        {filterType === 'month' ? (
          <div className="filter-group">
            <div className="form-group">
              <label htmlFor="ledger-year">Year</label>
              <select
                id="ledger-year"
                value={year}
                onChange={(e) => setYear(e.target.value)}
              >
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="ledger-month">Month</label>
              <select
                id="ledger-month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
              >
                <option value="1">January</option>
                <option value="2">February</option>
                <option value="3">March</option>
                <option value="4">April</option>
                <option value="5">May</option>
                <option value="6">June</option>
                <option value="7">July</option>
                <option value="8">August</option>
                <option value="9">September</option>
                <option value="10">October</option>
                <option value="11">November</option>
                <option value="12">December</option>
              </select>
            </div>
          </div>
        ) : (
          <div className="filter-group">
            <div className="form-group">
              <label htmlFor="start-date">Start Date</label>
              <input
                id="start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="end-date">End Date</label>
              <input
                id="end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            type="button"
            onClick={handleFilter}
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Get Ledger Data'}
          </button>
          <button
            type="button"
            onClick={handleExport}
            className="btn btn-secondary"
            disabled={exporting}
            title="Download the current filter as an Excel file"
          >
            {exporting ? 'Exporting…' : 'Export to Excel'}
          </button>
        </div>
      </div>

      {results && (
        <div>
          <div className="summary">
            <div className="summary-card">
              <h3>Total Incoming</h3>
              <div className="value amount-positive">
                {formatMoney(results.summary.totalIncoming)}
              </div>
              <p className="text-muted" style={{ fontSize: '12px', marginTop: '5px' }}>
                {results.summary.incomingCount} records
              </p>
            </div>
            <div className="summary-card">
              <h3>Total Outgoing</h3>
              <div className="value amount-negative">
                {formatMoney(results.summary.totalOutgoing)}
              </div>
              <p className="text-muted" style={{ fontSize: '12px', marginTop: '5px' }}>
                {results.summary.outgoingCount} records
              </p>
            </div>
            <div className="summary-card">
              <h3>Net Amount</h3>
              <div
                className={`value ${results.summary.netAmount >= 0
                    ? 'amount-positive'
                    : 'amount-negative'
                  }`}
              >
                {formatMoney(results.summary.netAmount)}
              </div>
            </div>
            <div className="summary-card">
              <h3>Total Records</h3>
              <div className="value">{results.summary.totalRecords}</div>
            </div>
          </div>

          <div className="card">
            <h3 style={{ marginBottom: '20px' }}>All Entries</h3>
            {results.entries.length === 0 ? (
              <p>No records found for the selected period</p>
            ) : (
              <div className="table-scroll">
              <table className="table">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>File No</th>
                    <th>Amount</th>
                    <th>Type</th>
                    <th>Reason for payment</th>
                    <th>Year</th>
                    <th>Month</th>
                    <th>Import date</th>
                  </tr>
                </thead>
                <tbody>
                  {results.entries.map((entry) => {
                    const isPositive = entry.type === 'incoming' || entry.type === 'transfer_in';
                    const isTransfer = entry.type === 'transfer_in' || entry.type === 'transfer_out';

                    return (
                      <tr key={entry.id}>
                        <td>{entry.no || '-'}</td>
                        <td>{entry.fileNo || '-'}</td>
                        <td
                          className={
                            isPositive
                              ? 'amount-positive'
                              : 'amount-negative'
                          }
                        >
                          {formatMoney(entry.amount)}
                        </td>
                        <td>
                          <span
                            className={
                              isPositive
                                ? isTransfer
                                  ? 'badge badge-transfer-in'
                                  : 'badge badge-incoming'
                                : isTransfer
                                  ? 'badge badge-transfer-out'
                                  : 'badge badge-outgoing'
                            }
                          >
                            {entry.type.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="text-muted" style={{ fontSize: '12px', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis' }} title={entry.reasonForPayment || ''}>
                          {entry.reasonForPayment || '-'}
                        </td>
                        <td>{entry.year}</td>
                        <td>
                          {new Date(entry.year, entry.month - 1).toLocaleString(
                            'default',
                            { month: 'long' }
                          )}
                        </td>
                        <td>
                          {new Date(entry.createdAt).toLocaleDateString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            )}
          </div>
        </div>
      )}

      {loading && <div className="loading">Loading ledger data...</div>}
    </div>
  );
};

export default Ledger;

